class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 1500px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** Remove only the listeners belonging to the desktop recommendation strip. */
  #vitrineCleanup = () => {};

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#setupVitrine();
    // CYGNUSS 2026-10-09: busca as sugestoes logo que a pagina termina de carregar (em tempo ocioso), para a secao
    // ja estar pronta quando a cliente chegar nela; antes so buscava a meia tela de distancia e ela via caixas cinzas.
    const cedo = () => {
      if (this.dataset.recommendationsPerformed === 'true' || !this.isConnected) return;
      this.#intersectionObserver.disconnect();
      this.#loadRecommendations();
    };
    const agenda = () => ('requestIdleCallback' in window ? requestIdleCallback(cedo, { timeout: 2500 }) : setTimeout(cedo, 1500));
    if (document.readyState === 'complete') agenda();
    else window.addEventListener('load', agenda, { once: true });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#vitrineCleanup();
  }

  /** Keep Shopify's original cards and tablet grid; only desktop scrolls horizontally. */
  #setupVitrine() {
    this.#vitrineCleanup();
    if (!this.closest('.content-for-layout[data-template^="product"]')) return;
    const grid = this.querySelector('.cyg-vitrine-recommendations');
    const controls = this.querySelector('.cyg-vitrine-navigation');
    if (!grid || !controls) return;

    const previous = controls.querySelector('[data-vitrine-direction="previous"]');
    const next = controls.querySelector('[data-vitrine-direction="next"]');
    if (!previous || !next) return;

    const desktop = window.matchMedia('(min-width: 1200px)');
    const listeners = new AbortController();
    const update = () => {
      if (!desktop.matches) grid.scrollLeft = 0;
      const canScroll = desktop.matches && grid.scrollWidth > grid.clientWidth + 2;
      controls.hidden = !canScroll;
      previous.disabled = !canScroll || grid.scrollLeft < 2;
      next.disabled = !canScroll || grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2;
    };

    controls.addEventListener('click', (event) => {
      const button = event.target.closest('[data-vitrine-direction]');
      if (!button || button.disabled || !desktop.matches) return;
      const direction = button.dataset.vitrineDirection === 'previous' ? -1 : 1;
      grid.scrollBy({
        left: direction * grid.clientWidth,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }, { signal: listeners.signal });
    grid.addEventListener('scroll', update, { passive: true, signal: listeners.signal });
    desktop.addEventListener('change', update, { signal: listeners.signal });
    const resize = new ResizeObserver(update);
    resize.observe(grid);
    this.#vitrineCleanup = () => {
      listeners.abort();
      resize.disconnect();
    };
    update();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#setupVitrine();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
